import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Typography, Button, Row, Col, Space, Spin, Tabs, message, Empty, Descriptions } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getResidentById, deleteResident, updateResident } from '@/api/residents';
import { getApartmentUnitById } from '@/api/properties';
import type { Resident } from '@/types/resources/resident';
import dayjs from 'dayjs';
import { useAuth } from '@/contexts/AuthContext';
import ConfirmDeleteModal from '@/components/modal/ConfirmDeleteModal';
import ContactsList from '@/components/common/ContactsList';
import ActivityLogList from '@/components/common/ActivityLogList';
import ResidentFormModal from '@/components/features/resources/residents/ResidentFormModal';
import { UserInfoTab, InvitationsTab } from '@/components/tab';

const { Title } = Typography;

const ResidentDetails: React.FC = () => {
  const { t } = useTranslation(['residents', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { resident: Resident } | null;
  const queryClient = useQueryClient();
  const { firestoreUser } = useAuth();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const shouldFetch = !state?.resident && !!id && !!firestoreUser;

  // Use state data if available, otherwise fetch
  const { data: resident, isLoading } = useQuery({
    queryKey: ['resident', id],
    queryFn: () => {
      if (!id || !firestoreUser) return null;
      return getResidentById(id, firestoreUser);
    },
    enabled: shouldFetch
  });

  // Combine state and fetched data
  const displayResident = state?.resident || resident;

  // Fetch apartment unit data only when apartment unit tab is active
  const { data: apartmentUnit, isLoading: apartmentUnitLoading } = useQuery({
    queryKey: ['apartmentUnit', displayResident?.apartmentUnit?.id],
    queryFn: () => {
      if (!displayResident?.apartmentUnit?.id || !firestoreUser) return null;
      return getApartmentUnitById(displayResident.apartmentUnit.id, firestoreUser);
    },
    enabled: activeTab === 'apartmentUnit' && !!displayResident?.apartmentUnit?.id && !!firestoreUser
  });

  const deleteMutation = useMutation({
    mutationFn: async (residentId: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteResident(residentId, firestoreUser);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['residents'] });
      message.success(t('residents:messages.deleteSuccess'));
      navigate('/resources/residents');
    },
    onError: (error: Error) => {
      message.error(error.message || t('residents:messages.deleteFailed'));
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Resident> }) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return updateResident(id, data, firestoreUser);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resident', id] });
      queryClient.invalidateQueries({ queryKey: ['residents'] });
      message.success(t('residents:messages.updateSuccess'));
      setIsEditModalOpen(false);
    },
    onError: (error: Error) => {
      message.error(error.message || t('residents:messages.updateFailed'));
    }
  });

  // Show loading only if no state data is available
  if (isLoading && !state?.resident) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayResident) {
    return <Card>{t('residents:notFound')}</Card>;
  }

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleEditSubmit = async (values: any) => {
    if (id) {
      updateMutation.mutate({ id, data: values });
    }
  };

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (id) {
      deleteMutation.mutate(id);
      setOpenDeleteDialog(false);
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const tabItems = [
    {
      key: 'overview',
      label: t('residents:common.details'),
      children: (
        <Card>
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('residents:fields.user')}>
              {displayResident.user?.displayName || displayResident.id}
            </Descriptions.Item>
            <Descriptions.Item label={t('residents:fields.unit')}>
              {displayResident.apartmentUnit?.number || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('residents:fields.type')}>
              {t(`residents:residencyTypes.${displayResident.type}`)}
            </Descriptions.Item>
            <Descriptions.Item label={t('residents:fields.moveInDate')}>
              {displayResident.moveInDate ? dayjs(displayResident.moveInDate).format('DD/MM/YYYY') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('residents:fields.status')}>
              {t(`residents:status.${displayResident.status}`)}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'apartmentUnit',
      label: t('residents:fields.unit'),
      children: (
        <Card>
          {apartmentUnitLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <Spin size="large" />
            </div>
          ) : apartmentUnit ? (
            <Descriptions bordered column={1}>
              <Descriptions.Item label={t('properties:fields.number')}>
                {apartmentUnit.number}
              </Descriptions.Item>
              <Descriptions.Item label={t('properties:fields.block')}>
                {apartmentUnit.block?.name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('properties:fields.floor')}>
                {apartmentUnit.floor?.name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('properties:fields.bedrooms')}>
                {apartmentUnit.bedrooms || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('properties:fields.bathrooms')}>
                {apartmentUnit.bathrooms || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('properties:fields.area')}>
                {apartmentUnit.area ? `${apartmentUnit.area} sq.ft` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('properties:fields.status')}>
                {t(`common:status.${apartmentUnit.status}`)}
              </Descriptions.Item>
              <Descriptions.Item label={t('properties:fields.description')}>
                {apartmentUnit.description || '-'}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <Empty description={t('residents:noApartmentUnit')} />
          )}
        </Card>
      ),
    },
    {
      key: 'user',
      label: t('residents:common.userInfo', 'User Info'),
      children: displayResident.user?.id ? (
        <UserInfoTab userId={displayResident.user.id} />
      ) : <Empty description={t('residents:notFound')} />,
    },
    {
      key: 'invitations',
      label: t('residents:common.invitations', 'Invitations'),
      children: displayResident.user?.id ? (
        <InvitationsTab userId={displayResident.user.id} userType="resident" />
      ) : <Empty description={t('residents:notFound')} />,
    },
    {
      key: 'contacts',
      label: t('residents:fields.contacts'),
      children: <ContactsList contacts={displayResident.contacts || []} />,
    },
    {
      key: 'activityLog',
      label: t('residents:common.activityLog'),
      children: <ActivityLogList activityRecords={displayResident.activityRecords || []} />,
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              {t('common:back')}
            </Button>
            <Title level={3} style={{ margin: 0 }}>{displayResident.user?.displayName || displayResident.id}</Title>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDeleteClick}>
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Content */}
      <Card>
        <Tabs 
          defaultActiveKey="overview" 
          items={tabItems} 
          onChange={handleTabChange}
          activeKey={activeTab}
        />
      </Card>

      {/* Edit Modal */}
      <ResidentFormModal
        visible={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        onSubmit={handleEditSubmit}
        initialValues={displayResident}
        title={t('residents:editTitle')}
      />

      <ConfirmDeleteModal
        open={openDeleteDialog}
        title={t('common:confirmDeleteTitle')}
        description={t('common:confirmDelete')}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default ResidentDetails; 